import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, Loader2, Check } from "lucide-react";

export function twoFactorError(error, t, fallback = "twofa.failed") {
  const detail = error?.response?.data?.detail;
  if (typeof detail !== "string") return t(fallback);
  if (detail.includes("Invalid") && detail.includes("2FA code")) return t("twofa.invalidCode");
  if (detail.includes("Set up 2FA")) return t("twofa.required");
  if (detail === "2FA is already enabled.") return t("twofa.alreadyEnabled");
  if (detail === "Start 2FA setup first.") return t("twofa.setupExpired");
  return detail || t(fallback);
}

function useTwoFactorStatus(active = true) {
  const [enabled, setEnabled] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = () => setAttempt((a) => a + 1);
  useEffect(() => {
    setEnabled(null);
    setFailed(false);
    if (!active) return;
    let current = true;
    api.get("/auth/2fa/status").then((r) => {
      if (current) setEnabled(r.data.enabled);
    }).catch(() => { if (current) setFailed(true); });
    return () => { current = false; };
  }, [active, attempt]);
  return { enabled, setEnabled, failed, retry };
}

function StatusError({ retry }) {
  const { t } = useTranslation();
  return <div className="space-y-3" role="alert">
    <p className="text-sm text-destructive">{t("twofa.statusFailed")}</p>
    <Button variant="outline" className="rounded-none" onClick={retry}>{t("twofa.retry")}</Button>
  </div>;
}

/** Inline 2FA enrolment block: QR + secret + activate. Calls onEnabled() when active. */
function SetupBlock({ onEnabled, testid }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const setupRequest = useRef(null);

  useEffect(() => {
    let current = true;
    setBusy(true);
    setFailed(false);
    // Reuse the request when StrictMode re-runs effects; do not rotate the pending key twice.
    if (!setupRequest.current) setupRequest.current = api.post("/auth/2fa/setup");
    setupRequest.current.then((r) => { if (current) setData(r.data); })
      .catch(() => { if (current) setFailed(true); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [attempt]);

  const retry = () => {
    setupRequest.current = null;
    setAttempt((a) => a + 1);
  };

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/activate", { code: code.trim() });
      toast.success(t("twofa.enabled"));
      onEnabled?.();
    } catch (e) {
      toast.error(twoFactorError(e, t));
    } finally { setBusy(false); }
  };

  if (!data) {
    if (failed) return <div className="space-y-3" role="alert">
      <p className="text-sm text-destructive">{t("twofa.setupFailed")}</p>
      <Button variant="outline" className="rounded-none" onClick={retry}>{t("twofa.retry")}</Button>
    </div>;
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" />{t("twofa.preparing")}</div>;
  }
  return (
    <div className="space-y-3" data-testid={`${testid}-setup`}>
      <p className="text-sm text-muted-foreground">{t("twofa.scanHint")}</p>
      <div className="flex flex-wrap items-start gap-4">
        <img src={`data:image/png;base64,${data.qr_png_base64}`} alt={t("twofa.qrAlt")} className="h-40 w-40 border border-border bg-white p-1" data-testid={`${testid}-qr`} />
        <div className="space-y-1 min-w-0">
          <Label className="font-mono text-[11px] uppercase text-muted-foreground">{t("twofa.manualKey")}</Label>
          <code className="block font-mono text-xs break-all bg-secondary p-2 border border-border">{data.secret}</code>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testid}-activate-code`} className="font-mono text-[11px] uppercase text-muted-foreground">{t("twofa.enterCode")}</Label>
        <div className="flex gap-2">
          <Input id={`${testid}-activate-code`} autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric" placeholder="123456" className="rounded-none font-mono tracking-widest max-w-[160px]" data-testid={`${testid}-activate-code`} />
          <Button onClick={activate} disabled={busy || code.length < 6} className="rounded-none" data-testid={`${testid}-activate`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("twofa.activate")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Settings card to manage account 2FA (enable / disable). */
export function TwoFactorCard() {
  const { t } = useTranslation();
  const { enabled, setEnabled, failed, retry } = useTwoFactorStatus();
  const [setupOpen, setSetupOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: code.trim() });
      toast.success(t("twofa.disabled"));
      setDisableOpen(false); setCode(""); setEnabled(false);
    } catch (e) {
      toast.error(twoFactorError(e, t));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid-panel p-5" data-testid="twofa-card">
      <div className="flex items-center gap-2 mb-1">
        {enabled ? <ShieldCheck className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4 text-yellow-600" />}
        <h3 className="font-display font-bold">{t("twofa.title")}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 max-w-2xl">{t("twofa.desc")}</p>
      {failed ? <StatusError retry={retry} /> : enabled === null ? (
        <p className="text-sm text-muted-foreground" role="status">{t("common.loading")}</p>
      ) : (
      <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-secondary px-4 py-3">
        <p className="text-sm flex items-center gap-1.5" data-testid="twofa-status">
          {enabled ? <><Check className="h-3.5 w-3.5 text-success" />{t("twofa.statusOn")}</> : t("twofa.statusOff")}
        </p>
        {enabled ? (
          <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => { setCode(""); setDisableOpen(true); }} data-testid="twofa-disable-button">{t("twofa.disable")}</Button>
        ) : (
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => setSetupOpen(true)} data-testid="twofa-setup-button">{t("twofa.setup")}</Button>
        )}
      </div>
      )}

      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="rounded-none max-w-md max-h-[90vh] overflow-y-auto" data-testid="twofa-setup-dialog">
          <DialogHeader><DialogTitle>{t("twofa.setupTitle")}</DialogTitle><DialogDescription>{t("twofa.scanHint")}</DialogDescription></DialogHeader>
          {setupOpen && <SetupBlock testid="twofa" onEnabled={() => { setSetupOpen(false); setEnabled(true); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={disableOpen} onOpenChange={(open) => { if (!busy) { setDisableOpen(open); if (!open) setCode(""); } }}>
        <DialogContent className="rounded-none max-w-sm" data-testid="twofa-disable-dialog">
          <DialogHeader><DialogTitle>{t("twofa.disableTitle")}</DialogTitle>
            <DialogDescription>{t("twofa.disableDesc")}</DialogDescription></DialogHeader>
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            aria-label={t("twofa.enterCode")} autoComplete="one-time-code" maxLength={6} inputMode="numeric" placeholder="123456" className="rounded-none font-mono tracking-widest" data-testid="twofa-disable-code" />
          <p className="text-xs text-muted-foreground">{t("twofa.freshCodeHint")}</p>
          <DialogFooter>
            <Button disabled={busy} variant="outline" className="rounded-none" onClick={() => { setDisableOpen(false); setCode(""); }}>{t("common.cancel")}</Button>
            <Button className="rounded-none bg-destructive hover:bg-destructive/90" onClick={disable} disabled={busy || code.length < 6} data-testid="twofa-disable-confirm">{t("twofa.disable")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Gate dialog used before a destructive action. If 2FA is off, it offers inline setup first;
 *  once enabled it asks for a code and calls onVerified(code). */
export function TwoFactorGate({ open, onOpenChange, title, description, onVerified, busy }) {
  const { t } = useTranslation();
  const { enabled, setEnabled, failed, retry } = useTwoFactorStatus(open);
  const [code, setCode] = useState("");

  useEffect(() => {
    if (open) {
      setCode("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!busy) onOpenChange(value); }}>
      <DialogContent className="rounded-none max-w-md max-h-[90vh] overflow-y-auto" data-testid="twofa-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" />{title || t("twofa.verifyTitle")}</DialogTitle>
          <DialogDescription>{description || t("twofa.enterCodeAction")}</DialogDescription>
        </DialogHeader>
        {failed ? <StatusError retry={retry} /> : enabled === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" />{t("common.loading")}</div>
        ) : enabled ? (
          <>
            <p className="text-sm text-muted-foreground">{t("twofa.enterCodeAction")}</p>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-label={t("twofa.enterCode")} autoComplete="one-time-code" maxLength={6} inputMode="numeric" placeholder="123456" className="rounded-none font-mono tracking-widest" data-testid="twofa-gate-code" autoFocus />
            <p className="text-xs text-muted-foreground">{t("twofa.freshCodeHint")}</p>
            <DialogFooter>
              <Button disabled={busy} variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
              <Button className="rounded-none" onClick={() => onVerified(code.trim())} disabled={busy || code.length < 6} data-testid="twofa-gate-verify">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("twofa.verify")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-sm border border-yellow-600/40 bg-yellow-50 text-yellow-800 p-3">{t("twofa.required")}</p>
            <SetupBlock testid="twofa-gate" onEnabled={() => setEnabled(true)} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
